import React from "react";
import { Autocomplete, TextField, Chip } from "@mui/material";

export interface UniAutocompleteItem {
    value: string;
    text: string;
}

export interface UniAutocompleteProps {
    items: UniAutocompleteItem[];
    class?: string;
    id?: string;
    defaultValue?: UniAutocompleteItem[] | null;
    value?: UniAutocompleteItem[];
    onChange?: (e: UniAutocompleteItem[]) => void;
    name?: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
}

/**
 * Custom Autocomplete component using MUI with multiple selection support.
 *
 * Can be used within forms to select multiple items from a list, while supporting validation,
 * disabling, and preselected default values.
 *
 * @component
 * @example
 * ```tsx
 * <UniAutocomplete
 *     items={[{ value: 'user1', text: 'Max' }, { value: 'user2', text: 'Moritz' }]}
 *     defaultValue={[{ value: 'user1', text: 'Max' }]}
 *     onChange={(val) => console.log(val)}
 *     placeholder="Select users"
 * />
 * ```
 *
 * @param {UniAutocompleteProps} props - Component properties
 * @returns {JSX.Element} Rendered Autocomplete field
 */
const UniAutocomplete: React.FC<UniAutocompleteProps> = (props) => {
    const [innerValue, setInnerValue] = React.useState<UniAutocompleteItem[]>(props.defaultValue || []);

    // Wenn controlled: Prop-Änderungen spiegeln
    React.useEffect(() => {
        if (props.value !== undefined) {
            setInnerValue(props.value);
        }
    }, [props.value]);

    // Wenn defaultValue sich ändert (uncontrolled-Usecase)
    React.useEffect(() => {
        if (props.value === undefined && props.defaultValue) {
            setInnerValue(props.defaultValue);
        }
    }, [props.defaultValue, props.value]);

    const handleChange = (_: any, newValue: UniAutocompleteItem[]) => {
        if (props.value === undefined) {
            setInnerValue(newValue); // uncontrolled pflegen
        }
        props.onChange?.(newValue);
    };

    const shownValue = props.value !== undefined ? props.value : innerValue;

    return (
        <Autocomplete
            multiple
            id={props.id || "uni-autocomplete"}
            options={props.items}
            disableCloseOnSelect
            getOptionLabel={(option) => option.text}
            value={shownValue}
            onChange={handleChange}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip variant="outlined" label={option.text} {...getTagProps({ index })} key={option.value} />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    className={props.class || "mb-3"}
                    name={props.name}
                    required={props.required}
                    disabled={props.disabled}
                    placeholder={props.placeholder || "Select"}
                    label={props.placeholder || "Select"}
                />
            )}
        />
    );
};

export default UniAutocomplete;
